import { useLang } from "../lib/i18n";
import { legalCopy } from "../lib/i18n/legal";
import { ScreenShell } from "./screen-shell";
import { H1, MonoMeta, TopBar } from "./ui/primitives";

type Props = {
  onBack: () => void;
};

export function TermsScreen({ onBack }: Props) {
  const { lang } = useLang();
  const t = legalCopy[lang].terms;

  return (
    <ScreenShell hideHeader>
      <div style={{ paddingTop: 6, paddingBottom: 18 }}>
        <TopBar back={t.topBarBack} title={t.topBarTitle} onBack={onBack} />
      </div>

      <article
        className="rounded-xl p-5 flex flex-col gap-4 leading-relaxed"
        style={{
          background: "var(--surface)",
          color: "var(--ink-2)",
          fontSize: 13,
          boxShadow: "0 0 0 1px var(--nav-border)",
        }}
      >
        <header>
          <H1 size={42} accent=".">
            {t.h1}
          </H1>
          <div style={{ marginTop: 6 }}>
            <MonoMeta>{t.metaUpdated}</MonoMeta>
          </div>
        </header>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s1Heading}</h3>
          <p>{t.s1Body}</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s2Heading}</h3>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>{t.s2ItemAge}</li>
            <li>{t.s2ItemAccount}</li>
            <li>{t.s2ItemHousehold}</li>
            <li>{t.s2ItemDelete}</li>
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s3Heading}</h3>
          <p>
            {t.s3Before}
            <strong>{t.s3Emph1}</strong>
            {t.s3Middle}
            <strong>{t.s3Emph2}</strong>
            {t.s3After}
          </p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s4Heading}</h3>
          <p>
            <strong>{t.s4IntroEmph}</strong>
            {t.s4IntroRest}
          </p>
          <ul className="list-disc pl-5">
            <li>
              {t.s4ItemAdultsLead}
              <strong>{t.s4ItemAdultsPhone}</strong>
            </li>
            <li>
              {t.s4ItemChildrenLead}
              <strong>{t.s4ItemChildrenPhone}</strong>
            </li>
            <li>
              {t.s4ItemEmergencyLead}
              <strong>{t.s4ItemEmergencyPhone}</strong>
            </li>
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s5Heading}</h3>
          <ul className="list-disc pl-5 flex flex-col gap-1">
            <li>{t.s5ItemReverse}</li>
            <li>{t.s5ItemUpload}</li>
            <li>{t.s5ItemPromote}</li>
            <li>{t.s5ItemSpam}</li>
          </ul>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s6Heading}</h3>
          <p>{t.s6Body1}</p>
          <p>{t.s6Body2}</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s7Heading}</h3>
          <p>{t.s7Body}</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s8Heading}</h3>
          <p>{t.s8Body}</p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="text-heading-3 text-ink-900">{t.s9Heading}</h3>
          <p>
            {t.s9Before}
            <a
              href="https://ec.europa.eu/consumers/odr"
              target="_blank"
              rel="noreferrer"
              className="text-moss-700 underline"
            >
              {t.s9LinkLabel}
            </a>
            {t.s9After}
          </p>
        </section>
      </article>
    </ScreenShell>
  );
}
